import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { authApi } from "../lib/authApi";
import { setCurrentUserRoles } from "../lib/errorReporter";
import { setAuthHint, clearAuthHint, shouldBootstrapAuth, hasAuthHint } from "../lib/authHint";
import { isEmbedRuntime } from "../lib/embedMode";

/**
 * AuthContext states
 * ─────────────────────────────────────────
 *   loading === true   → still checking cookie / /me
 *   user === null      → not authenticated
 *   user === { ... }   → authenticated
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // v20.9-I — mirror user vào ref cho listener online/visibility (tránh
  // stale closure / re-subscribe theo mỗi lần user đổi).
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  const refreshMe = useCallback(async (opts) => {
    try {
      const me = await authApi.me(opts);
      setUser(me);
      setAuthHint();
      return me;
    } catch (err) {
      // PR-B: chỉ clear hint khi 401/403 (mất phiên THẬT) — transient 5xx
      // KHÔNG được xoá hint để tránh "văng" user khi BE restart.
      const st = err?.response?.status;
      if (st === 401 || st === 403) {
        clearAuthHint();
        setUser(null);
        return null;
      }
      // v20.9-I (26/8/2026) — LỖI TRANSIENT (mất mạng / no-response / 5xx):
      // KHÔNG logout. Access cookie sống 30 ngày → blip mạng KHÔNG có nghĩa
      // hết phiên. Bug trước: setUser(null) mù → publisher mất mạng bị đá về
      // trang Đăng nhập, có mạng lại vẫn logout (không ai thử lại /auth/me).
      // Nếu ĐANG có hint (tin là đã đăng nhập) → giữ nguyên state + báo
      // transient để caller retry. Chỉ guest thật (không hint) mới về null.
      if (!hasAuthHint()) {
        setUser(null);
        return null;
      }
      return { __transient: true };
    }
  }, []);

  useEffect(() => {
    // Bootstrap: ask backend who we are (cookie-based). NO auto-login.
    // The dev auto-login bypass has been fully removed to eliminate the
    // risk of silently promoting production visitors to super_admin.
    // Phase 62: cookies live 30 days (access) + 90 days sliding (refresh)
    // so returning users stay logged in until they click Logout.
    // PR-B (GĐ2 audit): guest không có auth marker → skip hẳn /auth/me +
    // /auth/refresh (0 request auth mỗi page-load). Marker là HINT trong
    // localStorage, không phải nguồn xác thực — phiên cookie có sẵn trước
    // PR-B vẫn được probe đúng 1 lần (migration).
    // PR-A: iframe ?embed=1 → không bootstrap auth.
    if (isEmbedRuntime() || !shouldBootstrapAuth()) {
      setLoading(false);
      return;
    }
    (async () => {
      // P0.3 (GĐ2 audit) — probe migration (chưa có hint) chỉ được phép
      // 1 GET /auth/me, KHÔNG kèm POST /auth/refresh khi 401 (guest thật
      // = đúng 1 request duy nhất trong đời browser).
      const isProbe = !hasAuthHint();
      if (isProbe) {
        await refreshMe({ noRefresh: true });
        setLoading(false);
        return;
      }
      // v20.9-I — CÓ HINT = tin là đã đăng nhập. Bootstrap có thể chạy ngay
      // sau khi WebView reload lúc mạng chập chờn (vd renderer bị kill sau
      // ANR → auto-reload). Nếu /auth/me lỗi TRANSIENT → RETRY backoff thay
      // vì đá về Đăng nhập. Chỉ dừng khi thành công hoặc 401/403 thật.
      const maxBootRetries = 6;
      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await refreshMe();
        if (!res || !res.__transient) break; // success (user) | null (401 thật)
        attempt += 1;
        if (attempt >= maxBootRetries) break;
        const delay = Math.min(8000, 800 * Math.pow(2, attempt - 1));
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, delay));
      }
      setLoading(false);
    })();
  }, [refreshMe]);

  // v20.9-I — Khi mạng TRỞ LẠI (event `online`) hoặc app quay lại foreground
  // (`visibilitychange`) mà đang CÓ hint nhưng CHƯA có user (bị null do blip
  // mạng) → thử lại /auth/me. Đây là lưới cứu cho publisher: mất mạng xong
  // có mạng lại thì tự khôi phục phiên, không kẹt ở màn Đăng nhập.
  useEffect(() => {
    const recheck = () => {
      if (hasAuthHint() && !userRef.current) {
        refreshMe().catch(() => {});
      }
    };
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") recheck();
    };
    window.addEventListener("online", recheck);
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", recheck);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshMe]);

  // Phase 85.A.14 — Sync user roles vào errorReporter để gate floating
  // toast.error() (chỉ hiện cho privileged roles: admin/referee/organizer).
  useEffect(() => {
    setCurrentUserRoles(user?.roles || []);
  }, [user]);

  // Phase 62 — react to refresh failures from axios interceptor.
  useEffect(() => {
    const handler = () => {
      // PR-B — phiên chết thật (refresh 401/403) → xoá hint để lần load sau
      // không bắn auth request vô ích.
      clearAuthHint();
      // Phase 85.A.319 — ghi trail để chẩn đoán "trọng tài bị văng": mất phiên
      // (refresh thất bại) là tín hiệu HỆ THỐNG, không phải user bấm Back.
      import("../lib/refereeNavDiag")
        .then((m) => m.recordDiagEvent("session_invalidated"))
        .catch(() => {});
      // Phase 88.D — Khi trọng tài đang chấm trận (route /referee/*),
      // KHÔNG setUser(null) → tránh route guard đá văng về dashboard và mất
      // state trận đang dở. Thay vào đó fire event `auth:expired-while-scoring`
      // để RefereeMatchControl hiện modal đăng nhập lại tại chỗ.
      //
      // Phase 88.E.33 — Mở rộng guard: KHÔNG chỉ `/referee/match/*` mà MỌI
      // route `/referee/*` (vd `/referee/tournaments/:tid`, `/referee/pending`)
      // — BTC trọng tài có thể đang chọn trận khi BE restart → tránh kick
      // ra Dashboard. Thêm cả `/admin/*` (BTC đang chấm điểm + Force ELO).
      try {
        if (typeof window !== "undefined") {
          const path = window.location.pathname || "";
          if (path.startsWith("/referee") || path.startsWith("/admin")) {
            window.dispatchEvent(new CustomEvent("auth:expired-while-scoring"));
            return;
          }
        }
      } catch { /* fallthrough */ }
      setUser(null);
    };
    window.addEventListener("auth:expired", handler);
    return () => window.removeEventListener("auth:expired", handler);
  }, []);

  const login = useCallback(async (username, password) => {
    const me = await authApi.login(username, password);
    setUser(me);
    setAuthHint();
    return me;
  }, []);

  const register = useCallback(async (payload) => {
    const me = await authApi.register(payload);
    setUser(me);
    setAuthHint();
    return me;
  }, []);

  // Phase 85.A.185 — Gộp verify OTP + register thành 1 button click.
  const registerWithOtp = useCallback(async (payload) => {
    const me = await authApi.registerWithOtp(payload);
    setUser(me);
    setAuthHint();
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore; cookies cleared server-side anyway
    }
    // v16.17 Phase C.39 — Clear publisher watchdog armed flag khi logout để
    // native app không hijack navigate về /publisher/auto sau logout.
    try {
      localStorage.removeItem("pl.pub.auto.armed");
      localStorage.removeItem("pl.pub.native.lastActiveSession");
    } catch { /* noop */ }
    clearAuthHint();
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (...roles) => {
      if (!user) return false;
      if ((user.roles || []).includes("super_admin")) return true;
      return roles.some((r) => (user.roles || []).includes(r));
    },
    [user]
  );

  const hasPermission = useCallback(
    (...perms) => {
      if (!user) return false;
      if ((user.roles || []).includes("super_admin")) return true;
      return perms.every((p) => (user.permissions || []).includes(p));
    },
    [user]
  );

  // Phase 85.A.239 — quyền truy cập theo menu code (gate menu + route).
  const hasMenu = useCallback(
    (...codes) => {
      if (!user) return false;
      if ((user.roles || []).includes("super_admin")) return true;
      const access = user.menu_access || [];
      return codes.some((c) => access.includes(c));
    },
    [user]
  );

  const value = useMemo(
    () => ({ user, loading, login, register, registerWithOtp, logout, refreshMe, hasRole, hasPermission, hasMenu }),
    [user, loading, login, register, registerWithOtp, logout, refreshMe, hasRole, hasPermission, hasMenu]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
